import {confetti} from "@tsparticles/confetti";
import intlTelInput from "intl-tel-input";
import * as Sentry from "@sentry/browser";
import {Block, Notify} from "notiflix";

/**
 * Checks that an element has a non-empty `name` and `value` property.
 */
const isValidElement = (element: HTMLInputElement) => {
    const name = element.name.trim();
    const value = element.value.trim();
    return (name !== null && name !== undefined && name !== '') && (value !== null && value !== undefined && value !== '');
};

/**
 * Checks if an element’s value can be saved (e.g. not an unselected checkbox).
 */
const isValidValue = (element: HTMLInputElement) => {
    return !["checkbox", "radio"].includes(element.type) || element.checked;
};

/**
 * Checks if an input is a checkbox, because checkboxes allow multiple values.
 */
const isCheckbox = (element: HTMLInputElement) => element.type === "checkbox";

/**
 * Checks if an input is a `select` with the `multiple` attribute.
 */
const isMultiSelect = (element: HTMLSelectElement) => element.options && element.multiple;

/**
 * Get selected option values from a multi-select element.
 */
const getSelectValues = (options: HTMLOptionsCollection): string[] => Array.from(options).filter(opt => opt.selected).map(opt => opt.value);

/**
 * Convert form inputs to a JSON object.
 */
export const formToJSON = (elements: HTMLFormControlsCollection): Record<string, any> => {
    return Array.from(elements).reduce<Record<string, any>>((data, el) => {
        if (!isValidElement(el as HTMLInputElement) || !isValidValue(el as HTMLInputElement)) return data;

        const {name, value} = el as HTMLInputElement | HTMLSelectElement;

        if (isCheckbox(el as HTMLInputElement)) {
            (data[name] ??= []).push(value);
        } else if (isMultiSelect(el as HTMLSelectElement | any)) {
            data[name] = getSelectValues(el as HTMLOptionsCollection | any);
        } else {
            data[name] = value;
        }

        return data;
    }, {});
};

/**
 * @param {string} fileLink
 * @param {string} fileName
 * @returns {Promise<void>}
 */
export async function asyncDownloadFile(fileLink: string, fileName: string): Promise<void> {
    try {
        const response = await fetch(fileLink);

        if (!response.ok) {
            // noinspection ExceptionCaughtLocallyJS
            throw new Error(`Failed to fetch file: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const element = document.createElement('a');
        element.href = blobUrl;
        element.download = fileName;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        // free memory
        URL.revokeObjectURL(blobUrl);
    } catch (e) {
        console.error('[downloadX]', e);
        notifyError(getComonRequestMessage(true), {asFailure: true});
    }
}

export function toggleScroll(hide: boolean) {
    if (window.AppScroll) {
        hide ? window.AppScroll.stop() : window.AppScroll.start();
    } else {
        const html = document.querySelector('html');
        const body = document.querySelector('body');

        if (hide) {
            html.classList.add('overflow-hidden')
            body.classList.add('overflow-hidden')
        } else {
            html.classList.remove('overflow-hidden')
            body.classList.remove('overflow-hidden')
        }
    }
}

export function notifySuccess(message: string) {
    Notify.success(message);
}

interface NotifyErrorOptions {
    asFailure?: boolean;
    mode?: 'danger' | 'info'
}

export function notifyError(message: string, options?: NotifyErrorOptions) {
    const {asFailure = false, mode = 'danger'} = options || {}
    if (mode === 'danger') {
        Notify.merge({failure: {background: asFailure ? '#e3342f' : '#c7af7a', notiflixIconColor: '#FFF', textColor: '#FFF'}});
        Notify.failure(message);
    } else Notify.info(message);
}

export const getClientIpData = (useCache = true, fallback: any = null) => {
    return new Promise(function (resolve, reject) {

        const __fetch = () => fetch('https://ipapi.co/json').then((response) => response.json()).then((data) => {
            localStorage.setItem("__m_llt__", Date.now().toString()) //__m_llt__ => miabeloya last location timestamp;
            localStorage.setItem("__m_ald__", JSON.stringify(data)); //__m_ald__ => miabeloya auth location data
            return resolve(data)
        }).catch(() => reject(fallback))

        if (useCache) {
            const __now = Date.now();
            const last__fetched = Math.floor((__now - (parseFloat(localStorage.getItem("__m_llt__")) || 0)) / 60000);
            let refetch = last__fetched >= 5;

            if (refetch) return __fetch();
            else {
                console.log('location data fetched via cache ....');
                return resolve(JSON.parse(localStorage.getItem("__m_ald__")));
            }
        } else return __fetch();
    });
}

export const animateScrollTo = (
    element: HTMLElement,
    subtract: number = 80,
    animationDuration: number = 1000
) => {
    if (!element) return;

    const start = window.scrollY;
    const target = element.getBoundingClientRect().top + window.scrollY - subtract;
    const distance = target - start;
    let startTime: number | null = null;

    const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;

        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);

        window.scrollTo(0, start + distance * easeInOutCubic(progress));

        if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
};

export const renderIntlPhoneNumber = (
    element: HTMLInputElement,
    prefix = '+',
    validate = true,
    spaceBetweenDialCodeAndNumber = true,
    returnAsObject = false,
) => {
    try {
        const instance = intlTelInput.getInstance(element);
        let isValid = true;
        if (validate) isValid = isValidIntlPhoneNumber(element, instance);

        if (!isValid) return returnAsObject ? null : '';

        // noinspection JSUnresolvedReference
        const country = instance.getSelectedCountryData();

        const __number = element.value.replaceAll(' ', '');
        // noinspection JSUnresolvedReference
        const __prefix = `${prefix}${country.dialCode}`

        return returnAsObject ? {prefix: __prefix, number: __number} : `${__prefix}${spaceBetweenDialCodeAndNumber ? ' ' : ''}${__number}`;

    } catch (e) {
        console.error(e)
        return returnAsObject ? null : ''
    }
}

export const isValidIntlPhoneNumber = (element: HTMLInputElement, instance: any = null) => {
    try {
        // noinspection JSUnresolvedReference
        instance = instance ? instance : intlTelInput.getInstance(element);
        // noinspection JSUnresolvedReference
        return instance.isValidNumber();
    } catch (e) {
        console.error(e)
        return false;
    }
}

export const getComonRequestMessage = (isError: boolean = false, locale: string | null = null, errors: Record<string, string> | null = null) => {

    errors = errors !== null ? errors : {
        'fr': "Quelque chose c'est mal passé de notre côté. Merci de réessayer ou de contacter le service client si le problème persiste !",
        'en': "Something went wrong on our side. Please try again or contact customer service if the problem persists!"
    }

    const success: Record<string, string> = {
        'fr': 'Opération effectué avec succès !',
        'en': 'Operation successfully completed!'
    }

    locale = locale || 'fr';
    return isError ? errors[locale] : success[locale];
}

export const notifyCatchedException = (data?: any, sentryOnly: boolean = false) => {
    if (!sentryOnly) notifyError(getComonRequestMessage(true), {asFailure: true});
    if (data) notifySentryError(data);
}

interface RequestResponse {
    status: boolean;
    message: string;
    data?: Record<string, any>;

    [key: string]: any;
}

export async function fireRequest(
    route: string,
    data: any,
    uiBLockSelector: string,
    afterDataBuild?: (data: any) => Promise<any>,
    onSuccessResponse?: (data: any) => Promise<any>,
    onFaslyResponse?: (data: any) => Promise<any>,
): Promise<RequestResponse> {

    let __gps = {}
    const __locale = 'fr';

    let formNode: HTMLFormElement | null = null;
    const submitNode = document.querySelector(`${uiBLockSelector} button[type="submit"]`);
    if (submitNode) formNode = submitNode.closest('form')

    formNode?.classList.add('requesting');
    Block.pulse(uiBLockSelector);

    try {
        __gps = await getClientIpData(true);
    } catch (error) {
        console.warn(`Fail to get tracking data: ${error}`)
    }

    // await waitBro(5000)//For low latency connection testing purpose

    try {

        //Build request data
        let requestData = data
        //Add GPS and locale
        if (!(requestData instanceof FormData)) {
            requestData.gps = __gps;
            requestData.locale = __locale;
        } else {
            requestData.append('locale', __locale);
            requestData.append('gps', __gps as any)
        }

        //After build hook
        if (afterDataBuild) requestData = await afterDataBuild(requestData);

        console.log({requestData});

        //Determine URL
        if (!route) {
            // noinspection ExceptionCaughtLocallyJS
            throw new Error('No route action provided');
        }

        //Prepare fetch options
        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {}
        };

        const fHeaders = fetchOptions.headers as Record<string, string>;

        let parseAsJSON = true;

        //Handle JSON vs FormData
        if (requestData instanceof FormData) {
            // FormData (with files)
            // No need Content-Type - browser will set it automatically with boundary
            fetchOptions.body = requestData;
        } else {
            // JSON data
            fetchOptions.body = JSON.stringify(requestData);
            if (!('Content-Type' in fHeaders)) fHeaders['Content-Type'] = 'application/json';
            else parseAsJSON = false;
            fetchOptions.headers = fHeaders;
        }

        //Make fetch request
        const response = await fetch(route, fetchOptions);

        //Handle network errors
        if (!response.ok) {
            // noinspection ExceptionCaughtLocallyJS
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        //Parse response
        const result = parseAsJSON ? await response.json() : await response.text();

        //debug
        console.log({response: result, data: requestData});

        //Unblock UI


        if (!parseAsJSON) {
            formNode?.classList.remove('requesting');
            Block.remove(uiBLockSelector);
            return result;
        }

        //Handle successful response
        if (result.status) {
            notifySuccess(result.message);
            if (onSuccessResponse) await onSuccessResponse(result);
        } else {
            notifyError(result.message || getComonRequestMessage(true), {asFailure: !!result.fail});
            if (onFaslyResponse) await onFaslyResponse(result);
        }

        formNode?.classList.remove('requesting');
        Block.remove(uiBLockSelector);
        return result;

    } catch (error) {

        Block.remove(uiBLockSelector);
        formNode?.classList.remove('requesting');
        notifyError(getComonRequestMessage(true), {asFailure: true});

        console.error(`Error occurred when making request to: [${route}]...`);
        console.error({RequestError: error});
    }
}

export const fireConfetti = async (duration: number = 5000) => {
    const animationEnd = Date.now() + duration;
    const defaults = {startVelocity: 30, spread: 360, ticks: 60, zIndex: 0};

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(async function () {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) return clearInterval(interval);

        const particleCount = 50 * (timeLeft / duration);

        // since particles fall down, start a bit higher than random
        await confetti(
            Object.assign({}, defaults, {
                particleCount,
                origin: {x: randomInRange(0.1, 0.3), y: Math.random() - 0.2},
            })
        );

        await confetti(
            Object.assign({}, defaults, {
                particleCount,
                origin: {x: randomInRange(0.7, 0.9), y: Math.random() - 0.2},
            })
        );
    }, 250);
}

export const Utils = {
    getShimmer: () => document.querySelector<HTMLInputElement>('[name="shimmer__placeholder"]')?.value
}

export function isObject(element: any) {
    return typeof element === 'object' && element !== null
}

interface TryParseJSONOptions {
    fallback: any,
    throwErrorOnFail?: boolean
}

export function tryParseJSON(rawData: any, options?: TryParseJSONOptions) {
    const {fallback = null, throwErrorOnFail = true} = options || {};

    try {
        return isObject(rawData) ? rawData : JSON.parse(rawData);
    } catch (e) {
        if (throwErrorOnFail) console.error(`Error occured when parsing JSON :`, e)
        return fallback;
    }
}

export const isString = (value: unknown, strict: boolean = false) => typeof value === 'string' && (strict ? value.trim().length > 0 : true);

export const asString = (v: unknown, cast?: boolean, fallback: string = ''): string => (typeof v === 'string' ? v : (cast ? v + '' : fallback));

export const asNumber = (v: unknown, fallback: number = 0): number => (typeof v === 'number' ? v : (parseInt(v + '') ?? fallback));

// noinspection JSUnusedGlobalSymbols
export const autoPadNumber = (number: number | string, pad: number = 2): string => String(number).padStart(pad, "0")

export const openWindow = (url: string, {assignOnFail = true}: { assignOnFail: boolean }): boolean => {
    const state = window.open(url, '_blank');
    if (!state && assignOnFail) {
        window.location.assign(url);
        return true
    }
    return !!state;
}

// noinspection JSUnusedGlobalSymbols
export const hardReload = (url?: string, mode: 'replace' | 'assign' = 'replace') => {
    if (window !== undefined) {
        if (!url) window.location.reload();
        else if (mode === 'replace') window.location.replace(url)
        else window.location.assign(url)
    } else console.error('Hard reload failed... Window object is undefined. Client-Side Only')
}

// noinspection JSUnusedGlobalSymbols
export const waitBro = async (duration: number) => {
    return new Promise(resolve => setTimeout(resolve, duration))
}

// export const generateUUID = (short: boolean = false, length: number = 10): string => {
//     const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char: string): string => {
//         const random = window.crypto.getRandomValues(new Uint8Array(1))[0] % 16;
//         const value = char === 'x' ? random : (random & 0x3) | 0x8;
//         return value.toString(16);
//     });
//
//     return short ? shortenUUID(uuid, length) : uuid;
// };


export const shortenUUID = (uuid: string, length = 4): string => {
    const hex = uuid.replace(/-/g, "");
    return parseInt(hex.slice(0, 12), 16).toString(36).slice(0, length);
}


type DataSource = Record<string, unknown> | unknown[] | null | undefined;

export interface GetDatasetValueOptions<T> {
    defaultValue?: T | null,
    strictDefault?: boolean,
}

export const getDatasetValue = <T = unknown>(
    data: DataSource | object,
    key: string | number | Array<string | number>,
    {defaultValue = null, strictDefault = true}: GetDatasetValueOptions<T>,
): T | string | null => {
    const keys = Array.isArray(key) ? key : [key];

    const fallback: T | string | null = strictDefault ? (defaultValue as T) : (defaultValue ?? `{${keys.join(' | ')}}`);

    try {
        for (const k of keys) {
            if (data === null || data === undefined) continue;

            const value = (data as Record<string | number, unknown>)[k];

            if (value !== undefined && value !== null && value !== '') {
                return value as T;
            }
        }

        return fallback;
    } catch (error) {
        console.error(error);
        // captureException(error); // Sentry
        return fallback;
    }
};

export const notifySentryError = (error: any) => {
    Sentry.captureException(error);
}

interface WithSentyParams {
    name: string,
    extra?: string
    fetcher: () => Promise<any | void>
}

export const withSentry = async ({name, extra, fetcher}: WithSentyParams) => {
    await Sentry.startSpan(
        {name, op: extra},
        async () => {
            await fetcher();
        },
    );
}


export type DayPeriod = 'day' | 'night';

export type TimeWindow = {
    dayStartHour: number;
    dayEndHour  : number;
};

const DEFAULT_WINDOW: TimeWindow = {
    dayStartHour: 6,   // 06:00
    dayEndHour  : 18,  // 18:00 (6pm)
};

/**
 * Returns whether the current local time is day or night.
 * Day is defined as [dayStartHour, dayEndHour) by default 06:00–18:00.
 */
export const getDayPeriod = (
    date  : Date = new Date(),
    window: TimeWindow = DEFAULT_WINDOW
): DayPeriod => {
    const hour = date.getHours();
    return hour >= window.dayStartHour && hour < window.dayEndHour ? 'day' : 'night';
};

export const isDay   = (date?: Date, window?: TimeWindow): boolean => getDayPeriod(date, window) === 'day';

export const isNight = (date?: Date, window?: TimeWindow): boolean => getDayPeriod(date, window) === 'night';
